import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import Api from "../../../core/services/api/api";
import {bizFileAccessTokenResponseToFileInfo, setInMemoryBizFileUrlInfo,} from "../../../core/services/biz-file-urls/in-memory-biz-file-urls";
import {useBizFormApiResolver} from "../use-biz-form-api-resolver";
import {useAuthenticationState} from "../use-auth-state";
import {BizFileFileInfo, buildBizFilePreviewUrl, buildBizFilePublicPreviewUrl} from "../../../core/services/biz-file-urls/biz-file-file-info";

export type BizFileUrlChannelKey = "bizFile" | "ncrBizForm" | "operatorBizForm";

export interface BizFileUrlChannel {
    fileInfo: BizFileFileInfo | null;
    getFilePreviewUrl: (fileGuid?: string, fileExtension?: string) => string | undefined;
    getPublicFilePreviewUrl: (fileGuid: string, fileExtension?: string) => string | undefined;
}

export interface BizFileUrlsTokens {
    bizFile: BizFileUrlChannel;
    ncrBizForm: BizFileUrlChannel;
    operatorBizForm: BizFileUrlChannel;
}

const BizFileUrlsResolverContext = createContext<BizFileUrlsTokens | null>(null);

function makeChannel(fileInfo: BizFileFileInfo | null): BizFileUrlChannel {
    return {
        fileInfo,
        getFilePreviewUrl: (fileGuid?: string, fileExtension?: string) => buildBizFilePreviewUrl(fileInfo, fileGuid, fileExtension),
        getPublicFilePreviewUrl: (fileGuid: string, fileExtension?: string) => buildBizFilePublicPreviewUrl(fileInfo, fileGuid, fileExtension),
    };
}

function buildTokens(
    bizFile: BizFileFileInfo | null,
    ncrBizForm: BizFileFileInfo | null,
    operatorBizForm: BizFileFileInfo | null,
): BizFileUrlsTokens {
    return {
        bizFile: makeChannel(bizFile),
        ncrBizForm: makeChannel(ncrBizForm),
        operatorBizForm: makeChannel(operatorBizForm),
    };
}

/**
 * Provides in-memory BizFile preview credentials for app Biz File, NCR BizForm, and operator BizForm channels.
 */
export const BizFileUrlsResolverProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const {loggedIn, authenticationToken} = useAuthenticationState();
    const ncrBizFormApi = useBizFormApiResolver((t) => t.ncrService);
    const operatorBizFormApi = useBizFormApiResolver((t) => t.operatorApp);

    const [bizFile, setBizFile] = useState<BizFileFileInfo | null>(null);
    const [ncrBizForm, setNcrBizForm] = useState<BizFileFileInfo | null>(null);
    const [operatorBizForm, setOperatorBizForm] = useState<BizFileFileInfo | null>(null);

    const refreshAll = useCallback(async () => {
        if (!loggedIn || !authenticationToken) {
            setBizFile(null);
            setNcrBizForm(null);
            setOperatorBizForm(null);
            setInMemoryBizFileUrlInfo("bizFile", null);
            setInMemoryBizFileUrlInfo("ncrBizForm", null);
            setInMemoryBizFileUrlInfo("operatorBizForm", null);
            return;
        }
        const [appRes, ncrRes, opRes] = await Promise.all([
            Api.createBizFileAccessToken(authenticationToken),
            ncrBizFormApi.createBizFileAccessToken(authenticationToken),
            operatorBizFormApi.createBizFileAccessToken(authenticationToken),
        ]);
        const nextBiz = appRes?.resultFlag ? bizFileAccessTokenResponseToFileInfo(appRes.data) : null;
        const nextNcr = ncrRes?.resultFlag ? bizFileAccessTokenResponseToFileInfo(ncrRes.data) : null;
        const nextOp = opRes?.resultFlag ? bizFileAccessTokenResponseToFileInfo(opRes.data) : null;
        setBizFile(nextBiz);
        setNcrBizForm(nextNcr);
        setOperatorBizForm(nextOp);
        setInMemoryBizFileUrlInfo("bizFile", nextBiz);
        setInMemoryBizFileUrlInfo("ncrBizForm", nextNcr);
        setInMemoryBizFileUrlInfo("operatorBizForm", nextOp);
    }, [loggedIn, authenticationToken, ncrBizFormApi, operatorBizFormApi]);

    /**
     * Refreshes all channels on mount and whenever auth state changes.
     */
    useEffect(() => {
        void refreshAll();
    }, [refreshAll]);

    const tokens = useMemo(() => buildTokens(bizFile, ncrBizForm, operatorBizForm), [bizFile, ncrBizForm, operatorBizForm]);

    return (
        <BizFileUrlsResolverContext.Provider value={tokens}>
            {children}
        </BizFileUrlsResolverContext.Provider>
    );
};

/**
 * Selects a BizFile URL channel (e.g. {@code t => t.ncrBizForm}).
 */
export function useBizFileUrlsResolver<T>(select: (tokens: BizFileUrlsTokens) => T): T {
    const tokens = useContext(BizFileUrlsResolverContext);
    if (!tokens) {
        throw new Error("useBizFileUrlsResolver must be used within BizFileUrlsResolverProvider");
    }
    return select(tokens);
}
